import { Island, Sheet, SheetStack } from "@silk-hq/components"
import PropTypes from "prop-types"
import React, { Fragment, useEffect, useId, useMemo } from "react"
import { createPortal } from "react-dom"

import { ModalPortalContext } from "./ModalPortalContext"

const ModalMobile = ({
  onClose,
  children,
  isOpen,
  afterLeave,
  initialFocusRef,
  stacked,
  dismissable = true,
}) => {
  // stacked={true} means this modal is a child on top of another modal.
  // In that case, omit SheetStack.Root so this sheet participates in the
  // parent modal's stack context (triggering the iOS scale-back animation).
  // stacked={false} (default) means this is a root-level modal — it wraps
  // with SheetStack.Root so any future stacked child can participate in it.
  const Wrapper = stacked ? Fragment : SheetStack.Root

  const portalIdBase = useId()
  const portalIds = useMemo(
    () => ({
      datePicker: `${portalIdBase}datepicker`,
      timePicker: `${portalIdBase}timepicker`,
    }),
    [portalIdBase]
  )

  // The Intercom launcher is fixed bottom-right and overlaps the sheet's
  // footer buttons while a full-screen modal is presented. Snapshot the
  // prior value so we don't reveal the launcher in contexts that had it
  // hidden already (e.g., PWA standalone mode, in-app Chat panel).
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return undefined

    const previousHidden = window.intercomSettings?.hide_default_launcher
    window.intercomSettings = {
      ...window.intercomSettings,
      hide_default_launcher: true,
    }
    window.Intercom?.("update", window.intercomSettings)

    return () => {
      window.intercomSettings = {
        ...window.intercomSettings,
        hide_default_launcher: previousHidden ?? false,
      }
      window.Intercom?.("update", window.intercomSettings)
    }
  }, [isOpen])

  return (
    <Wrapper>
      {/* MOB-1130: Silk dismisses on a click outside the sheet, judged by
          element containment, and react-datepicker portals its calendar to a
          shared `#datepicker-root` on document.body that can predate the
          sheet. Pickers inside this modal get per-modal host ids instead, and
          the hosts sit in an Island, which Silk counts as inside.

          Getting the calendar to paint above the sheet takes three things:
          the Island is portaled to body, because the app root is
          `isolation: isolate`; the hosts are position: fixed, because Silk
          forces Island.Root to a zero-height `overflow: auto !important`
          box that clips the calendar otherwise; and they skip Island.Content,
          whose position: sticky wrapper would trap the z-index beneath the
          sheet. */}
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <Island.Root>
            <div
              id={portalIds.datePicker}
              className="fixed left-0 top-0 z-tooltip"
            />
            <div
              id={portalIds.timePicker}
              className="fixed left-0 top-0 z-tooltip"
            />
          </Island.Root>,
          document.body
        )}
      <Sheet.Root
        license="commercial"
        presented={isOpen}
        onPresentedChange={(open) => {
          if (!open && dismissable) onClose?.()
        }}
      >
        <Sheet.Portal>
          <Sheet.View
            className={stacked ? "z-modal-stacked" : "z-modal"}
            contentPlacement="bottom"
            swipe={false}
            nativeEdgeSwipePrevention
            onEscapeKeyDown={{ dismiss: dismissable }}
            onClickOutside={{ dismiss: dismissable }}
            onTravelStatusChange={(status) => {
              if (status === "idleOutside") afterLeave?.()
              if (status === "idleInside") initialFocusRef?.current?.focus()
            }}
            style={{
              height: "calc(100lvh + 60px)",
              top: "calc(env(safe-area-inset-top, 0px) - 1px)",
            }}
          >
            <Sheet.Backdrop
              className={`fixed inset-0 ${
                dismissable ? "bg-black/50" : "bg-black/60 backdrop-blur-sm"
              }`}
            />
            <Sheet.Content className="flex h-full w-full flex-col bg-white shadow-xl">
              {/* Constrain to the visible viewport. dvh (not lvh) tracks the
                  current viewport so the footer stays reachable on Android
                  Chrome and iOS Safari while the URL bar is showing. +1px
                  corrects the -1px top offset on Sheet.View so the wrapper
                  fills exactly the screen. */}
              <div
                className="flex flex-col overflow-hidden"
                style={{
                  height: "calc(100dvh - env(safe-area-inset-top, 0px) + 1px)",
                }}
              >
                {dismissable && (
                  <div className="flex h-12 shrink-0 items-center justify-end border-b border-gray-100 px-4">
                    <Sheet.Trigger
                      action="dismiss"
                      className="bg-transparent text-2xl font-semibold text-gray-700 hover:text-gray-500 focus:outline-none focus:ring"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </Sheet.Trigger>
                  </div>
                )}
                <div className="flex flex-1 flex-col overflow-hidden p-4">
                  <ModalPortalContext.Provider value={portalIds}>
                    {children}
                  </ModalPortalContext.Provider>
                </div>
              </div>
            </Sheet.Content>
          </Sheet.View>
        </Sheet.Portal>
      </Sheet.Root>
    </Wrapper>
  )
}

ModalMobile.propTypes = {
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool.isRequired,
  afterLeave: PropTypes.func,
  initialFocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  stacked: PropTypes.bool,
  dismissable: PropTypes.bool,
}

export default ModalMobile
